// ═══════════════════════════════════════════════════
// ADMIN PRODUCT CATALOG — manual project creation
// ═══════════════════════════════════════════════════
//
// Flattens the client-facing video catalog (lib/pricing.js) into a single
// searchable list for the admin "New Project" form, and builds line items in
// the EXACT shape a client-placed order writes.
//
// WHY THE LABELS MUST MATCH BYTE-FOR-BYTE:
// `line_items[].label` is the de-facto join key for every downstream product
// decision — editor pay rate lookup (backend/app/editor_pay.py:24, plus two
// hand-maintained copies in server.py), must-haves category resolution
// (server.py:2667 / lib/mustHaves.js), photo-vs-video project splitting
// (server.py:3503), and analytics bucketing (routes/analytics.py:739).
// Nothing keys off a product id — the id is thrown away at cart-build time and
// the backend reverse-derives identity by sniffing the label string.
//
// So these labels are reproduced verbatim from NewOrderPage.js:472-488. The
// em dash in the highlight labels is REQUIRED: server.py:4475 matches
// `^Highlight — (Music Only|Music \+ Dialogue) \((\d+) min\)$`. It is a
// data join key, not customer-facing prose, so the house no-em-dash rule does
// not apply here — changing it silently breaks pay matching.
//
// Photo products are intentionally excluded: their labels embed runtime image
// counts (`Culling (1,200 images × $0.06)`) and app/photo_counts.py depends on
// a matching `image_count`. The admin form has no count input, so photo
// one-offs go through the Custom escape hatch.

import {
  CULLING_PRODUCT,
  SOCIAL_MEDIA_PRODUCTS,
  HIGHLIGHT_MUSIC_ONLY,
  HIGHLIGHT_MUSIC_DIALOGUE,
  DOC_EDIT_PRODUCTS,
  NLE_OPTIONS,
} from '@/lib/pricing';

export const CUSTOM_PRODUCT_KEY = '__custom__';

// Marker key for the one product whose label needs an extra operator input.
export const CULLING_KEY = 'culling-only';

/**
 * Flat, searchable catalog of every VIDEO product a client can order.
 * Each entry: { key, group, name, price, buildLineItem(opts) }
 * `buildLineItem` returns the line-item dict POSTed as line_items[0].
 */
export const ADMIN_VIDEO_CATALOG = [
  {
    key: CULLING_KEY,
    group: 'Culling',
    name: 'Culling Only',
    price: CULLING_PRODUCT.price,
    needsNle: true,
    buildLineItem: ({ nle }) => ({
      label: `Culling Only (${nle || 'NLE TBD'})`,
      amount: CULLING_PRODUCT.price,
      editor_pay: CULLING_PRODUCT.editor_pay,
      turnaround_days: CULLING_PRODUCT.turnaround_days,
      is_doc_edit: false,
    }),
  },

  ...SOCIAL_MEDIA_PRODUCTS.map(p => ({
    key: p.id,
    group: 'Social Media',
    name: `${p.name} (${p.duration})`,
    price: p.price,
    buildLineItem: () => ({
      label: `${p.name} (${p.duration})`,
      amount: p.price,
      editor_pay: p.editor_pay,
      turnaround_days: p.turnaround_days,
      is_doc_edit: false,
    }),
  })),

  ...HIGHLIGHT_MUSIC_ONLY.map(r => ({
    key: `hl-music-only-${r.minutes}`,
    group: 'Highlight — Music Only',
    name: `Highlight — Music Only (${r.minutes} min)`,
    price: r.price,
    buildLineItem: () => ({
      label: `Highlight — Music Only (${r.minutes} min)`,
      amount: r.price,
      editor_pay: r.editor_pay,
      turnaround_days: r.turnaround_days,
      is_doc_edit: false,
      duration_minutes: r.minutes,
    }),
  })),

  ...HIGHLIGHT_MUSIC_DIALOGUE.map(r => ({
    key: `hl-dialogue-${r.minutes}`,
    group: 'Highlight — Music + Dialogue',
    name: `Highlight — Music + Dialogue (${r.minutes} min)`,
    price: r.price,
    buildLineItem: () => ({
      label: `Highlight — Music + Dialogue (${r.minutes} min)`,
      amount: r.price,
      editor_pay: r.editor_pay,
      turnaround_days: r.turnaround_days,
      is_doc_edit: false,
      duration_minutes: r.minutes,
    }),
  })),

  ...DOC_EDIT_PRODUCTS.map(p => ({
    key: p.id,
    group: 'Doc Edits',
    name: p.name,
    price: p.price,
    buildLineItem: () => ({
      label: p.name,
      amount: p.price,
      editor_pay: p.editor_pay,
      turnaround_days: p.turnaround_days,
      is_doc_edit: true,
    }),
  })),
];

export const findCatalogProduct = key => ADMIN_VIDEO_CATALOG.find(p => p.key === key) || null;

export { NLE_OPTIONS };
